if (!customElements.get('location-status')) {
  customElements.define(
    'location-status',
    class LocationStatus extends HTMLElement {
      constructor() {
        super();
        this.refCache = new Map();
        this.isResolvingName = false;
        this._locationsDocumentPromise = null;
        this._initialContentSnapshot = null;
        this._productForm = null;
        this._locationDrawer = null;
        this._updateTimeout = null;

        this.variantChangeUnsubscriber = undefined;
        this.domVariantChangedHandler = undefined;

        this._onLocationChanged = this.onLocationChanged.bind(this);
      }

      connectedCallback() {
        if (!this.isConnected) return;

        // Prevent layout impact from the wrapper element.
        if (!this.style.display) this.style.display = 'contents';

        const locationText = this.getLocationTextNode();
        if (locationText && this.defaultLocationText == null) {
          this.defaultLocationText = locationText.textContent;
        }

        // Snapshot the initial content (used by PDP pickup/delivery options UI).
        const content = this.refs('content');
        if (content && !this._initialContentSnapshot) {
          this._initialContentSnapshot = content.cloneNode(true);
        }

        document.addEventListener('location:changed', this._onLocationChanged);

        // If this instance renders pickup/delivery stock status, re-render on variant changes.
        if (this.refs('content') && this.refs('locationStatusTemplate')) {
          this.variantChangeUnsubscriber = window.FoxTheme?.pubsub?.subscribe?.(
            window.FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
            this._debounceUpdate.bind(this)
          );

          if (!this.variantChangeUnsubscriber) {
            this.domVariantChangedHandler = this._debounceUpdate.bind(this);
            document.addEventListener('variant:changed', this.domVariantChangedHandler);
          }
        }

        this._debounceUpdate = this._debounceUpdate.bind(this);
        document.addEventListener('location:update', this._debounceUpdate);

        this._debounceUpdate();
      }

      disconnectedCallback() {
        document.removeEventListener('location:changed', this._onLocationChanged);
        document.removeEventListener('location:update', this._debounceUpdate);

        this.variantChangeUnsubscriber?.();
        this.variantChangeUnsubscriber = undefined;

        if (this.domVariantChangedHandler) {
          document.removeEventListener('variant:changed', this.domVariantChangedHandler);
          this.domVariantChangedHandler = undefined;
        }

        // Clear timeout and cleanup
        if (this._updateTimeout) {
          clearTimeout(this._updateTimeout);
          this._updateTimeout = null;
        }

        // Clear cache
        this.refCache.clear();
        this._productForm = null;
        this._locationDrawer = null;
        this._locationsDocumentPromise = null;
      }

      refs(value) {
        if (this.refCache.has(value)) {
          return this.refCache.get(value);
        }
        const element = this.querySelector(`[ref='${value}']`);
        if (element) {
          this.refCache.set(value, element);
        }
        return element;
      }


      get productForm() {
        if (!this._productForm) {
          this._productForm = this.closest('.product-form')?.querySelector("form[is='product-form'], form[ref='main-pdp-form']");
        }
        return this._productForm;
      }

      getLocationDataFromItem(item) {
        if (!item) return null;
        if (item.__locationData) return item.__locationData;

        const script = item.querySelector('script[type="application/json"]');
        const raw = script?.textContent?.trim();
        if (!raw) return null;

        try {
          const parsed = JSON.parse(raw);
          item.__locationData = parsed;
          return parsed;
        } catch (error) {
          this._logError('Failed to parse location data', error);
          return null;
        }
      }

      _logError(message, error) {
        if (typeof console !== 'undefined' && console.error) {
          console.error(`[LocationStatus] ${message}:`, error);
        }
      }

      _debounceUpdate() {
        if (this._updateTimeout) {
          clearTimeout(this._updateTimeout);
        }
        this._updateTimeout = setTimeout(() => {
          this._updateTimeout = null;
          this.updateFromCookie();
        }, 100);
      }

      // Re-render the selected-store panel and re-dispatch the submit-button state
      // immediately, bypassing the 100ms debounce. Called synchronously on variant
      // change (product-info.js) so the pickup stock status and the Add-to-Cart button
      // reflect the newly selected variant without lagging behind the debounce/AJAX
      // race that left the button stale after switching variants (FPS-614).
      refreshNow() {
        if (this._updateTimeout) {
          clearTimeout(this._updateTimeout);
          this._updateTimeout = null;
        }
        this.updateFromCookie();
      }

      isSameLatLng(latA, lngA, latB, lngB) {
        const aLat = Number(latA);
        const aLng = Number(lngA);
        const bLat = Number(latB);
        const bLng = Number(lngB);

        if (![aLat, aLng, bLat, bLng].some((n) => Number.isNaN(n))) {
          return Math.abs(aLat - bLat) < 1e-6 && Math.abs(aLng - bLng) < 1e-6;
        }

        return String(latA) === String(latB) && String(lngA) === String(lngB);
      }
      onLocationChanged() {
        this.refCache.clear();
        this._debounceUpdate();
      }

      async updateFromCookie() {
        const saved = this.getSelectedLocationFromCookie();
        // Header UI: update the location label (supports both ref="locationText" and ref="locationName").
        const locationText = this.getLocationTextNode();

        if (locationText) {
          if (!saved) {
            if (this.defaultLocationText != null) locationText.textContent = this.defaultLocationText;
            const headerIcon = this.refs('headerIcon');
            if (headerIcon) headerIcon.classList.remove('header-icon--solid');
          } else {
            const name = saved?.name?.trim();
            if (name) {
              locationText.textContent = name;
              this.updateHeaderIcon();
            } else if (saved.latitude && saved.longitude) {
              // Backfill: old cookies only stored lat/lng; try to resolve the name.
              const resolved = await this.resolveLocationName(saved.latitude, saved.longitude);
              if (resolved) {
                this.setSelectedLocationCookie({ ...saved, name: resolved });
                locationText.textContent = resolved;
                this.updateHeaderIcon();
              }
            }
          }
        }

        // PDP delivery options UI: optionally render the selected store panel.
        await this.updatePdpContent(saved);
      }

      updateHeaderIcon() {
        const headerIcon = this.refs('headerIcon');
        if (headerIcon) {
          headerIcon.classList.add('header-icon--solid');
        }
      }


      getLocationTextNode() {
        // Header snippet uses ref="locationName", but older markup used ref="locationText".
        return this.refs('locationName') || this.refs('locationText');
      }

      async updatePdpContent(saved) {
        const content = this.refs('content');
        const statusTemplate = this.refs('locationStatusTemplate');

        if (!content || !statusTemplate) {
          return;
        }

        // No store selected: restore initial markup.
        if (!saved) {
          if (this._initialContentSnapshot) {
            content.replaceChildren(...Array.from(this._initialContentSnapshot.childNodes).map((n) => n.cloneNode(true)));
            this.refCache.clear();
          } else {
          }
          this.#dispatchSubmitButtonUpdate(false);
          return;
        }

        // Render selected-store template.
        content.replaceChildren(statusTemplate.content.cloneNode(true));
        this.refCache.clear();

        const deliveryMethod = this.getDeliveryMethod();

        if (deliveryMethod != 'pickup') {
          this._updateLocationDetails(saved);
        }
        else {
          const locationData = this.resolveLocationDetails(saved);
          if (!locationData) {
            this.#dispatchSubmitButtonUpdate(false);
            return;
          }

          this._updateLocationDetails(locationData);
          this.#dispatchSubmitButtonUpdate(locationData.pickupStatus === true);
        }
      }

      _updateLocationDetails(locationData) {
        const nameNode = this.refs('locationName') || this.refs('locationText');

        if (nameNode && locationData.name) {
          nameNode.textContent = locationData.name;
        }

        const openHoursNode = this.refs('locationOpenHours');
        if (openHoursNode && locationData.hoursOfOperation) {
          openHoursNode.textContent = locationData.hoursOfOperation;
        }

        const statusNode = this.refs('locationStatus');
        if (statusNode && typeof locationData.pickupStatus === 'boolean') {
          const inStockTemplate = this.refs('inStockTemplate');
          const outOfStockTemplate = this.refs('outOfStockTemplate');

          if (inStockTemplate && outOfStockTemplate) {
            const template = locationData.pickupStatus ? inStockTemplate : outOfStockTemplate;
            statusNode.replaceChildren(template.content.cloneNode(true));
          }
        }
      }


      resolveLocationDetails(saved) {
        const locationData = this.#parseInlineLocationData();
        if (!locationData) return null;

        const entry = this.#findLocationEntry(locationData, saved);
        if (!entry) return null;

        return {
          name: entry.name || saved?.name?.trim() || null,
          pickupStatus: typeof entry.available === 'boolean' ? entry.available : null,
          hoursOfOperation: this.#resolveOpenHoursText(entry.hoursOfOperation),
        };
      }

      #formatTime(timeStr) {
        if (!timeStr) return timeStr;
        const [h, m] = timeStr.split(':').map(Number);
        if (isNaN(h) || isNaN(m)) return timeStr;
        const period = h >= 12 ? 'PM' : 'AM';
        const hour = h % 12 || 12;
        return `${hour}:${String(m).padStart(2, '0')} ${period}`;
      }

      #resolveOpenHoursText(hoursOfOperation) {
        if (!hoursOfOperation || typeof hoursOfOperation !== 'object') return null;

        const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const todayName = days[new Date().getDay()];
        const todayHours = hoursOfOperation[todayName];

        if (!todayHours?.close) return null;

        const formattedClose = this.#formatTime(todayHours.close);
        const template = this.dataset.i18nOpenUntil;

        if (template) return template.replace('__TIME__', formattedClose);

        return formattedClose;
      }

      #parseInlineLocationData() {
        const script = this.querySelector('script[type="application/json"]');
        const raw = script?.textContent?.trim();
        if (!raw) return null;

        try {
          return JSON.parse(raw);
        } catch (error) {
          this._logError('Failed to parse inline location data', error);
          return null;
        }
      }

      #findLocationEntry(data, saved) {
        if (!Array.isArray(data) || !saved) return null;

        if (saved.latitude && saved.longitude) {
          const match = data.find((entry) =>
            this.isSameLatLng(entry.latlng?.latitude, entry.latlng?.longitude, saved.latitude, saved.longitude)
          );
          if (match) return match;
        }

        if (saved.name) {
          return data.find((entry) => entry.name === saved.name) || null;
        }

        return null;
      }

      #dispatchSubmitButtonUpdate(enable) {
        if (!this.#isPickupSelected()) return;

        // FPS-705: delegate to delivery-options' own dispatchSubmitButtonUpdate(), the single
        // source of truth that also checks selling_plan for AutoOrder. This used to fire its own
        // pickup-stock-only event here, racing the correct one and sometimes re-enabling
        // Add to Cart while AutoOrder was selected with no selling_plan resolved yet.
        const deliveryOptions = this.closest('delivery-options');
        if (deliveryOptions?.dispatchSubmitButtonUpdate) {
          deliveryOptions.dispatchSubmitButtonUpdate();
          return;
        }

        const event = new CustomEvent('update:submitButton', {
          bubbles: true,
          detail: { enable },
        });

        this.dispatchEvent(event);

        const productForm = this.productForm;
        if (!productForm) return;
        productForm.dispatchEvent(new CustomEvent('update:submitButton', { detail: { enable } }));
      }

      #isPickupSelected() {
        const deliveryOptions = this.closest('delivery-options');
        const selectedOption = deliveryOptions?.querySelector('input[type="radio"][name^="delivery-option"]:checked');
        return selectedOption?.value === 'pickup';
      }

      async getLocationsDocument() {
        if (this._locationsDocumentPromise) return this._locationsDocumentPromise;

        const drawer = this.locationDrawer;
        const fetchUrl = drawer?.dataset?.fetchLocationsUrl;

        if (!fetchUrl) return null;

        this._locationsDocumentPromise = (async () => {
          const response = await fetch(fetchUrl);
          if (!response.ok) return null;

          const html = await response.text();
          return new DOMParser().parseFromString(html, 'text/html');
        })();

        return this._locationsDocumentPromise;
      }

      findLocationItem(doc, latitude, longitude) {
        const items = doc.querySelectorAll('[ref="locationItem"]');
        for (const item of items) {
          const data = this.getLocationDataFromItem(item);
          if (!data) continue;
          if (!this.isSameLatLng(data?.latitude, data?.longitude, latitude, longitude)) continue;
          return item;
        }
        return null;
      }


      async resolveLocationName(latitude, longitude) {
        if (this.isResolvingName) return null;

        this.isResolvingName = true;

        try {
          const doc = await this.getLocationsDocument();
          if (!doc) return null;

          const item = this.findLocationItem(doc, latitude, longitude);
          if (!item) return null;

          const data = this.getLocationDataFromItem(item);
          const nameFromData = String(data?.name ?? '').trim();
          const nameFromDom = item.querySelector('.list-locations__item-name')?.textContent?.trim() || '';
          return (nameFromDom || nameFromData).trim() || null;
        } catch (error) {
          this._logError('Failed to resolve location name', error);
          return null;
        } finally {
          this.isResolvingName = false;
        }
      }

      setSelectedLocationCookie(value) {
        try {
          const encoded = encodeURIComponent(JSON.stringify(value));
          const maxAge = this.cookieMaxAge;
          const maxAgePart = Number.isFinite(maxAge) && maxAge > 0 ? `; Max-Age=${maxAge}` : '';

          document.cookie = `locationDrawerSelected=${encoded}; path=/; SameSite=Lax${maxAgePart}`;

          document.dispatchEvent(
            new CustomEvent('location:changed', {
              detail: value,
              bubbles: true,
            })
          );
        } catch (error) {
          this._logError('Failed to set location cookie', error);
        }
      }

      getDeliveryMethod() {
        return (this.closest('.shopify-section') || document).querySelector('input[name="properties[_delivery_method]"]')?.value || 'pickup';

      }

      getSelectedLocationFromCookie() {
        const cookies = document.cookie ? document.cookie.split('; ') : [];
        const deliveryMethod = this.getDeliveryMethod();
        const prefix = deliveryMethod === 'pickup' ? 'locationDrawerSelected=' : 'addressDrawerResult=';
        const cookie = cookies.find((entry) => entry.startsWith(prefix));
        if (!cookie) return null;

        const value = cookie.substring(prefix.length);
        if (!value) return null;

        try {
          const parsed = JSON.parse(decodeURIComponent(value));
          if (parsed && typeof parsed === 'object' && !parsed.name && parsed.formatted_address) {
            parsed.name = parsed.formatted_address;
          }
          return parsed;
        } catch (error) {
          this._logError('Failed to parse location cookie', error);
          return null;
        }
      }

      get locationDrawer() {
        if (!this._locationDrawer) {
          this._locationDrawer = document.getElementById('LocationDrawer');
        }
        return this._locationDrawer;
      }

      get cookieMaxAge() {
        const maxAge = Number(this.locationDrawer?.dataset?.cookieMaxAge);
        return Number.isNaN(maxAge) ? null : maxAge;
      }

    }
  );
}
